import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Building2, Server } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail, api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { BrandLogo } from "@/components/BrandLogo";
import { MfaChallengeStep } from "@/components/mfa/MfaChallengeStep";

export default function Login() {
  const { login, register, applyAuthenticatedUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const isDemo = params.get("demo") === "1";
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [role, setRole] = useState(["server", "provider"].includes(params.get("role")) ? "server" : "client");
  const [busy, setBusy] = useState(false);
  const [mfaStep, setMfaStep] = useState(null); // { challengeToken, enrollmentRequired }
  const [form, setForm] = useState({
    email: "", password: "", name: "", phone: "", address: "", firm_name: "", license_number: "", background_info: "", service_capability: "process_server",
    legal_name: "", dba: "", entity_type: "", state_of_formation: "", accept_user_agreement: false,
  });
  const [agreement, setAgreement] = useState(null); // { content, notice } when open

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openAgreement = async () => {
    try {
      const { data } = await api.get("/user-agreement/preview", { params: { legal_name: form.legal_name } });
      setAgreement(data);
    } catch { toast.error("Could not load the agreement."); }
  };

  React.useEffect(() => {
    if (params.get("reason") === "inactivity") {
      toast.info("You were signed out due to 10 minutes of inactivity. Please sign in again.");
    }
  }, [params]);

  const gotoDashboard = (u) => {
    // Return-after-login: only ever honor an internal same-origin path (no open redirect).
    const n = params.get("next") || "";
    let safe = null;
    try {
      const dec = decodeURIComponent(n);
      if (dec.startsWith("/") && !dec.startsWith("//") && !dec.startsWith("/\\")) safe = dec;
    } catch { safe = null; }
    nav(safe || (u?.role === "admin" ? "/admin" : u?.role === "server" ? "/server" : "/client"));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let user;
      if (mode === "login") {
        user = await login(form.email, form.password);
        if (user?.mfa_required) {
          setMfaStep({ challengeToken: user.challenge_token, enrollmentRequired: !!user.mfa_enrollment_required });
          return;
        }
      } else {
        user = await register({ ...form, role });
      }
      toast.success(`Welcome, ${user.name}`);
      gotoDashboard(user);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden w-1/2 flex-col justify-between bg-slate-900 p-12 lg:flex">
        <div className="flex items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white p-1.5">
            <BrandLogo className="h-full w-auto" />
          </div>
          <p className="font-display text-base font-extrabold leading-tight tracking-tight text-white">Audit Ready<br />Process Service</p>
        </div>
        <div>
          <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-white">
            The chain of custody<br />starts here.
          </h2>
          <p className="mt-4 max-w-md text-slate-400">
            GPS coordinates, timestamped photos, and court-ready affidavits — every service attempt, permanently on the record.
          </p>
        </div>
        <p data-testid="brand-tagline" className="font-mono-arps text-xs text-slate-500">"Every Service. Every Attempt. Every Detail. Documented."</p>
      </div>

      <div className="flex w-full items-center justify-center px-5 py-10 lg:w-1/2">
        <div className="w-full max-w-md fade-up">
          <div className="mb-6 flex items-center gap-3 lg:hidden">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white p-1.5">
              <BrandLogo className="h-full w-auto" />
            </div>
            <p className="font-display text-base font-extrabold leading-tight tracking-tight text-slate-900">Audit Ready<br />Process Service</p>
          </div>
          <button onClick={() => nav("/")} className="mb-6 text-sm text-slate-400 hover:text-slate-700">← Back to home</button>

          {mfaStep ? (
            <MfaChallengeStep
              challengeToken={mfaStep.challengeToken}
              enrollmentRequired={mfaStep.enrollmentRequired}
              onSuccess={(u) => { if (u) applyAuthenticatedUser(u); toast.success("Signed in"); gotoDashboard(u); }}
            />
          ) : (
          <>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">
            {mode === "login" ? "Sign in" : "Create your account"}
          </h1>
          <p className="mt-1.5 text-slate-500">
            {mode === "login"
              ? (isDemo ? <>Access your ARPS <span className="font-bold text-red-600">DEMO</span> portal.</> : "Access your ARPS portal.")
              : "Are you ordering a service, or applying to work as a provider?"}
          </p>

          {mode === "register" && (
            <div className="mt-5 grid grid-cols-2 gap-3">
              {[
                { key: "client", label: "I need a service", sub: "Process serving, notary, skip trace", icon: Building2 },
                { key: "server", label: "I'm a provider", sub: "Apply to serve or notarize for ARPS", icon: Server },
              ].map((r) => (
                <button
                  key={r.key}
                  type="button"
                  data-testid={`role-select-${r.key}`}
                  onClick={() => setRole(r.key)}
                  className={`flex flex-col items-center gap-1.5 rounded-xl border p-4 text-center text-sm font-semibold transition ${
                    role === r.key ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-300"
                  }`}
                >
                  <r.icon className="h-5 w-5" />
                  <span>{r.label}</span>
                  <span className="text-[11px] font-normal leading-tight opacity-70">{r.sub}</span>
                </button>
              ))}
            </div>
          )}

          {mode === "register" && role === "server" && (
            <div className="mt-4">
              <Label>Professional classification <span className="text-red-500">*</span></Label>
              <div className="mt-1.5 grid grid-cols-3 gap-2" data-testid="capability-select">
                {[
                  { key: "process_server", label: "Process Server" },
                  { key: "notary", label: "Notary" },
                  { key: "both", label: "Both" },
                ].map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    data-testid={`capability-${c.key}`}
                    onClick={() => setForm({ ...form, service_capability: c.key })}
                    className={`rounded-lg border px-2 py-2 text-xs font-semibold transition ${
                      form.service_capability === c.key ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {mode === "register" && (
            <p className="mt-4 text-xs text-slate-400">Fields marked <span className="font-semibold text-red-500">*</span> are required.</p>
          )}

          <form onSubmit={submit} className="mt-3 space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Full legal name {role === "server" && <span className="text-red-500">*</span>}</Label>
                <Input data-testid="input-name" id="name" required value={form.name} onChange={set("name")} placeholder={role === "server" ? "Jane R. Server" : "Your full name"} />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email {mode === "register" && role === "server" && <span className="text-red-500">*</span>}</Label>
              <Input data-testid="input-email" id="email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" />
            </div>

            {mode === "register" && (
              <div>
                <Label htmlFor="phone">Phone {role === "server" && <span className="text-red-500">*</span>}</Label>
                <Input data-testid="input-phone" id="phone" required={role === "server"} value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" />
              </div>
            )}
            {mode === "register" && role === "server" && (
              <div>
                <Label htmlFor="address">Address <span className="text-red-500">*</span></Label>
                <Input data-testid="input-address" id="address" required value={form.address} onChange={set("address")} placeholder="123 Field Ave, Denver, CO 80202" />
              </div>
            )}
            {mode === "register" && role === "client" && (
              <div>
                <Label htmlFor="firm">Company / law firm (optional)</Label>
                <Input data-testid="input-firm" id="firm" value={form.firm_name} onChange={set("firm_name")} placeholder="Your company or firm" />
              </div>
            )}

            <div>
              <Label htmlFor="password">Password {mode === "register" && role === "server" && <span className="text-red-500">*</span>}</Label>
              <PasswordInput data-testid="input-password" id="password" required value={form.password} onChange={set("password")} placeholder="••••••••" />
            </div>

            {mode === "register" && role === "server" && (
              <div data-testid="server-onboarding-note" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                <p className="font-semibold">Next step: document onboarding</p>
                <p className="mt-1 leading-relaxed">After you create your account you'll upload required credentials (Government ID, Process Server License/Certification, Surety Bond, Liability/E&O Insurance, Independent Contractor Agreement, W-9). Your account stays <strong>Pending Approval</strong> until an admin reviews and approves them — you won't receive assignments before then.</p>
                {form.service_capability === "notary" && <p className="mt-1">As a <strong>Notary</strong>, ARPS will review your notary commission during onboarding.</p>}
                {form.service_capability === "both" && <p className="mt-1">You selected <strong>both</strong> capabilities — ARPS will review your process-server and notary credentials.</p>}
              </div>
            )}

            {mode === "login" && (
              <div className="flex items-center justify-between text-sm">
                <button type="button" data-testid="forgot-password-link" onClick={() => nav("/forgot-password")} className="font-medium text-emerald-600 hover:underline">Forgot password?</button>
                <button type="button" data-testid="forgot-username-link" onClick={() => nav("/forgot-password?mode=username")} className="font-medium text-slate-500 hover:underline">Forgot username?</button>
              </div>
            )}
            <div className="text-center text-sm">
              <button type="button" data-testid="login-support-link" onClick={() => window.dispatchEvent(new Event("open-support"))} className="font-medium text-slate-500 hover:underline">Need help? Contact support</button>
            </div>

            {mode === "register" && role === "server" && (
              <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div>
                  <Label htmlFor="legal_name">Legal Name <span className="text-red-500">*</span></Label>
                  <Input data-testid="input-legal-name" id="legal_name" required value={form.legal_name} onChange={set("legal_name")} placeholder="Your full personal or business legal name" />
                  <p className="mt-1 text-xs text-slate-500">Used as the Subscriber's legal name on the ARPS Platform Subscriber Agreement.</p>
                </div>
                <div>
                  <Label htmlFor="dba">DBA / Trade Name <span className="text-xs font-normal text-slate-400">(optional)</span></Label>
                  <Input data-testid="input-dba" id="dba" value={form.dba} onChange={set("dba")} placeholder="If you operate under a DBA / trade name" />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="entity_type">Business Entity Type <span className="text-xs font-normal text-slate-400">(optional)</span></Label>
                    <select data-testid="input-entity-type" id="entity_type" value={form.entity_type} onChange={set("entity_type")} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                      <option value="">Select…</option>
                      <option value="Individual/Sole Proprietor">Individual / Sole Proprietor</option>
                      <option value="LLC">LLC</option>
                      <option value="Corporation">Corporation</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="state_of_formation">State of Formation <span className="text-xs font-normal text-slate-400">(if entity)</span></Label>
                    <Input data-testid="input-state-of-formation" id="state_of_formation" value={form.state_of_formation} onChange={set("state_of_formation")} placeholder="e.g. Tennessee" />
                  </div>
                </div>
                <label className="flex items-start gap-2 text-sm text-slate-700">
                  <input data-testid="accept-user-agreement" type="checkbox" checked={form.accept_user_agreement} onChange={(e) => setForm({ ...form, accept_user_agreement: e.target.checked })} className="mt-1" />
                  <span>I have read and accept the <button type="button" data-testid="view-user-agreement" onClick={openAgreement} className="font-semibold text-emerald-600 underline">ARPS Platform Subscriber Agreement</button>.</span>
                </label>
              </div>
            )}

            <Button data-testid="submit-auth-button" type="submit" disabled={busy || (mode === "register" && role === "server" && !form.accept_user_agreement)} className="w-full bg-slate-900 text-white hover:bg-slate-800">
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          {agreement && (
            <div data-testid="user-agreement-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setAgreement(null)}>
              <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-slate-200 p-4">
                  <h3 className="font-display text-lg font-bold text-slate-900">{agreement.title}</h3>
                  <button data-testid="close-user-agreement" onClick={() => setAgreement(null)} className="text-slate-400 hover:text-slate-700">✕</button>
                </div>
                {agreement.notice && <p className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-700">{agreement.notice}</p>}
                <pre data-testid="user-agreement-content" className="flex-1 overflow-auto whitespace-pre-wrap p-4 font-sans text-xs leading-relaxed text-slate-700">{agreement.content}</pre>
              </div>
            </div>
          )}

          {!isDemo && (
          <p className="mt-5 text-center text-sm text-slate-500">
            {mode === "login" ? "New to ARPS?" : "Already have an account?"}{" "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="font-semibold text-emerald-600 hover:underline"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}
